import type { IntelSourceCategory } from '../../../types/intel-source.js';

export interface PlatformDef {
  slug: string;
  name: string;
  description: string;
  category: IntelSourceCategory;
  icon: string;
}

export interface PlatformGroup {
  category: IntelSourceCategory;
  label: string;
  platforms: PlatformDef[];
}

const SOCIAL_PLATFORMS: PlatformDef[] = [
  {
    slug: 'youtube',
    name: 'YouTube',
    description: 'Video content creators and analysts',
    category: 'social',
    icon: 'youtube',
  },
  {
    slug: 'twitter',
    name: 'Twitter / X',
    description: 'Real-time market commentary and signals',
    category: 'social',
    icon: 'twitter',
  },
  {
    slug: 'reddit',
    name: 'Reddit',
    description: 'Community discussions and sentiment',
    category: 'social',
    icon: 'message-circle',
  },
  {
    slug: 'tradingview',
    name: 'TradingView',
    description: 'Technical analysis and chart ideas',
    category: 'social',
    icon: 'line-chart',
  },
  {
    slug: 'telegram',
    name: 'Telegram',
    description: 'Signal groups and alpha channels',
    category: 'social',
    icon: 'send',
  },
  {
    slug: 'discord',
    name: 'Discord',
    description: 'Community servers and trading groups',
    category: 'social',
    icon: 'message-square',
  },
];

const ON_CHAIN_PLATFORMS: PlatformDef[] = [
  {
    slug: 'wallet',
    name: 'Wallet',
    description: 'Track any on-chain wallet address',
    category: 'on_chain',
    icon: 'wallet',
  },
  {
    slug: 'smart_money',
    name: 'Smart Money',
    description: 'Known profitable wallets and funds',
    category: 'on_chain',
    icon: 'brain',
  },
  {
    slug: 'exchange_wallet',
    name: 'Exchange Wallet',
    description: 'CEX hot/cold wallet flows',
    category: 'on_chain',
    icon: 'building',
  },
  {
    slug: 'dao_treasury',
    name: 'DAO Treasury',
    description: 'Protocol treasury holdings and moves',
    category: 'on_chain',
    icon: 'landmark',
  },
];

const DEFI_PLATFORMS: PlatformDef[] = [
  {
    slug: 'lending',
    name: 'Lending',
    description: 'Aave, Compound, Morpho rates and liquidations',
    category: 'defi',
    icon: 'percent',
  },
  { slug: 'dex', name: 'DEX', description: 'Uniswap, Curve, Balancer pool activity', category: 'defi', icon: 'repeat' },
  {
    slug: 'perp_dex',
    name: 'Perp DEX',
    description: 'GMX, dYdX, Hyperliquid positions',
    category: 'defi',
    icon: 'trending-up',
  },
  {
    slug: 'options',
    name: 'Options',
    description: 'Derive, Lyra, Premia options flow',
    category: 'defi',
    icon: 'bar-chart-2',
  },
  {
    slug: 'yield',
    name: 'Yield',
    description: 'Yield farming opportunities and TVL',
    category: 'defi',
    icon: 'sprout',
  },
  {
    slug: 'governance',
    name: 'Governance',
    description: 'Protocol votes and proposals',
    category: 'defi',
    icon: 'vote',
  },
];

const INSTITUTIONAL_PLATFORMS: PlatformDef[] = [
  {
    slug: 'etf',
    name: 'ETF',
    description: 'Bitcoin and crypto ETF flow data',
    category: 'institutional',
    icon: 'bar-chart',
  },
  {
    slug: 'hedge_fund',
    name: 'Hedge Fund',
    description: 'Institutional fund positions and filings',
    category: 'institutional',
    icon: 'briefcase',
  },
  {
    slug: 'market_maker',
    name: 'Market Maker',
    description: 'Market maker inventory and activity',
    category: 'institutional',
    icon: 'activity',
  },
  {
    slug: 'vc',
    name: 'VC',
    description: 'Venture capital investments and unlocks',
    category: 'institutional',
    icon: 'rocket',
  },
];

const RESEARCH_PLATFORMS: PlatformDef[] = [
  {
    slug: 'research_firm',
    name: 'Research Firm',
    description: 'Professional crypto research reports',
    category: 'research',
    icon: 'file-text',
  },
  {
    slug: 'analytics',
    name: 'Analytics',
    description: 'On-chain and market analytics platforms',
    category: 'research',
    icon: 'pie-chart',
  },
  {
    slug: 'data_aggregator',
    name: 'Data Aggregator',
    description: 'Aggregated market data feeds',
    category: 'research',
    icon: 'database',
  },
];

const MARKET_INFRA_PLATFORMS: PlatformDef[] = [
  {
    slug: 'cex',
    name: 'CEX',
    description: 'Centralized exchange announcements and data',
    category: 'market_infra',
    icon: 'server',
  },
  {
    slug: 'prediction_market',
    name: 'Prediction Market',
    description: 'Polymarket, Kalshi event contracts',
    category: 'market_infra',
    icon: 'target',
  },
  {
    slug: 'news_wire',
    name: 'News Wire',
    description: 'Breaking news and press releases',
    category: 'market_infra',
    icon: 'newspaper',
  },
];

export const PLATFORM_GROUPS: PlatformGroup[] = [
  { category: 'social', label: 'Social', platforms: SOCIAL_PLATFORMS },
  { category: 'on_chain', label: 'On-Chain', platforms: ON_CHAIN_PLATFORMS },
  { category: 'defi', label: 'DeFi', platforms: DEFI_PLATFORMS },
  { category: 'institutional', label: 'Institutional', platforms: INSTITUTIONAL_PLATFORMS },
  { category: 'research', label: 'Research', platforms: RESEARCH_PLATFORMS },
  { category: 'market_infra', label: 'Market Infra', platforms: MARKET_INFRA_PLATFORMS },
];

export const ALL_PLATFORMS: PlatformDef[] = PLATFORM_GROUPS.flatMap((g) => g.platforms);

export function findPlatform(slug: string): PlatformDef | undefined {
  return ALL_PLATFORMS.find((p) => p.slug === slug);
}

export const CHAIN_OPTIONS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'base', label: 'Base' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'optimism', label: 'Optimism' },
  { value: 'bsc', label: 'BSC' },
  { value: 'solana', label: 'Solana' },
] as const;

export const ON_CHAIN_TAGS = [
  { value: 'whale', label: 'Whale' },
  { value: 'smart_money', label: 'Smart Money' },
  { value: 'dex_trader', label: 'DEX Trader' },
  { value: 'mev', label: 'MEV' },
  { value: 'dao', label: 'DAO' },
] as const;

export const INSTITUTIONAL_TYPES = [
  { value: 'etf', label: 'ETF' },
  { value: 'hedge_fund', label: 'Hedge Fund' },
  { value: 'market_maker', label: 'Market Maker' },
  { value: 'vc', label: 'Venture Capital' },
  { value: 'public_company', label: 'Public Company' },
] as const;
